/**
 * A row of named options drawn as buttons, exactly one of them pressed.
 *
 * The primitive two surfaces had each written by hand: the settings screen's choice control and the
 * seed picker's archetype and tier rows are the same `radiogroup`, the same `aria-checked`, the
 * same every-press-unpresses-the-others walk. The second copy arrived by reading the first, which
 * is the signal this file exists to answer.
 *
 * Native buttons in a `radiogroup`, so the row arrives keyboard reachable and screen-reader
 * nameable without any of that being written here. `aria-checked` carries which one is on, and the
 * `is-chosen` class is the same fact drawn: a player using either channel gets the answer.
 *
 * The classes are the caller's, because the accent is per surface. A surface hands in the class its
 * stylesheet draws the group with and the one it draws each button with, and keeps its own rules;
 * the one class this file owns is `is-chosen`, because which option is on is the row's own fact.
 *
 * What is deliberately not here: a visible label, because the two surfaces put theirs in different
 * places, and any writing to disk, because a settings knob commits and a picker row is only read
 * when the form goes.
 */

import { el, setClass } from '@/ui/dom';

/** One option, as the player reads it. `value` is stable; `label` is wording and may change. */
export interface OptionRowChoice<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface OptionRowOptions<T extends string> {
  /** What a screen reader announces the group as. The visible label is the caller's row. */
  readonly label: string;
  readonly choices: readonly OptionRowChoice<T>[];
  /** Which option opens pressed. A value no choice carries opens the row with none chosen. */
  readonly initial: T;
  /** The class the caller's stylesheet draws the `radiogroup` with. */
  readonly groupClass: string;
  /** The class each button carries, beside the `is-chosen` this file owns. */
  readonly optionClass: string;
  /** The player picked one. Absent for a row that is only read when its form submits. */
  readonly onPick?: (value: T) => void;
}

export interface OptionRowHandle<T extends string> {
  /** The `radiogroup` itself. The caller appends it and never reaches inside. */
  readonly root: HTMLElement;
  /** Which option is on right now. */
  chosen(): T;
  dispose(): void;
}

export function createOptionRow<T extends string>(
  options: OptionRowOptions<T>,
): OptionRowHandle<T> {
  const root = el('div', options.groupClass);
  root.role = 'radiogroup';
  root.ariaLabel = options.label;

  let chosen: T = options.initial;

  // Built by pushing rather than by mapping, so a handler can walk the finished list: every press
  // unpresses the others, and the row cannot end up with two options showing as chosen.
  const buttons: PressedButton<T>[] = [];
  for (const choice of options.choices) {
    const button = el('button', options.optionClass, choice.label);
    button.type = 'button';
    button.role = 'radio';
    const press = (): void => {
      chosen = choice.value;
      for (const other of buttons) setChosen(other.button, other.value === choice.value);
      options.onPick?.(choice.value);
    };
    button.addEventListener('click', press);
    root.append(button);
    buttons.push({ value: choice.value, button, press });
  }

  for (const { value, button } of buttons) setChosen(button, value === chosen);

  return {
    root,
    chosen: (): T => chosen,
    dispose(): void {
      for (const { button, press } of buttons) button.removeEventListener('click', press);
      root.remove();
    },
  };
}

/** One option's button, and the listener that has to come off it again. */
interface PressedButton<T extends string> {
  readonly value: T;
  readonly button: HTMLButtonElement;
  readonly press: () => void;
}

/** Both channels at once: the class the stylesheet draws, and the state a screen reader reads. */
function setChosen(button: HTMLButtonElement, chosen: boolean): void {
  setClass(button, 'is-chosen', chosen);
  button.ariaChecked = String(chosen);
}
